// OAuth sign-in helpers. Supabase Auth handles the redirect dance for the
// four standard providers; Autodesk APS is wired as a custom OAuth provider
// in the Supabase dashboard (manual endpoint URLs).
//
// All flows redirect back to /auth/callback on the current origin, where
// Supabase's detectSessionInUrl picks up the code and finalizes the session.

import { supabase } from './supabase';
import type { OAuthProvider } from './types';
import { BlockedEmailDomainError, emailDomain, isBlockedEmailDomain } from './blockedDomains';

const PROVIDER_LABELS: Record<OAuthProvider, string> = {
  google:        'Continue with Google',
  apple:         'Continue with Apple',
  linkedin_oidc: 'Continue with LinkedIn',
  dropbox:       'Continue with Dropbox',
  autodesk:      'Continue with Autodesk',
};

// Supabase Custom OAuth providers are registered with a `custom:<slug>` identifier.
// The UI OAuthProvider name ('autodesk') is the user-facing label; the value below
// is the literal identifier registered in the Supabase dashboard.
const SUPABASE_PROVIDER_ID: Record<OAuthProvider, string> = {
  google:        'google',
  apple:         'apple',
  linkedin_oidc: 'linkedin_oidc',
  dropbox:       'dropbox',
  autodesk:      'custom:autodesk-aps',
};

export function providerLabel(p: OAuthProvider): string {
  return PROVIDER_LABELS[p];
}

export const ALL_PROVIDERS: OAuthProvider[] = [
  'google', 'apple', 'linkedin_oidc', 'dropbox', 'autodesk',
];

export async function signInWith(provider: OAuthProvider): Promise<void> {
  const redirectTo = `${window.location.origin}${import.meta.env.BASE_URL}auth/callback`;
  const id = SUPABASE_PROVIDER_ID[provider];
  const { error } = await supabase.auth.signInWithOAuth({
    provider: id as Parameters<typeof supabase.auth.signInWithOAuth>[0]['provider'],
    options: { redirectTo },
  });
  if (error) throw error;
}

export async function signInWithEmail(email: string, password: string): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
}

export async function signUpWithEmail(email: string, password: string): Promise<void> {
  // Client-side blocklist check — the server-side trigger in migration 0005
  // is authoritative, but failing fast here means the user doesn't wait on
  // a round-trip + cryptic Postgres error for a known-blocked domain.
  if (isBlockedEmailDomain(email)) throw new BlockedEmailDomainError(emailDomain(email));

  const redirectTo = `${window.location.origin}${import.meta.env.BASE_URL}auth/callback`;
  const { error } = await supabase.auth.signUp({
    email, password,
    options: { emailRedirectTo: redirectTo },
  });
  if (error) {
    // Translate the server-side trigger error into the same UI message.
    // Postgres surfaces it as: `email_domain_not_allowed: <domain> ...`
    const msg = error.message ?? '';
    const m = /email_domain_not_allowed:\s*([^\s]+)/.exec(msg);
    if (m) throw new BlockedEmailDomainError(m[1]);
    throw error;
  }
}
